import React, { useEffect, useState, useRef, useCallback } from "react";
import { io } from "socket.io-client";
import "./Chat.css";
import "./chat/chatFeatures.css";
import Navbar from "./Navbar";

import { BASE_URL } from "./chat/constants";
import { makeClientId, upsertMessage } from "./chat/chatUtils";
import * as chatApi from "./chat/chatApi";

import UserList from "./chat/UserList";
import ChatHeader from "./chat/ChatHeader";
import MessageBubble from "./chat/MessageBubble";
import MessageInput from "./chat/MessageInput";
import ForwardModal from "./chat/ForwardModal";
import BackupDialog from "./chat/BackupDialog";
import { useCall } from "./chat/CallProvider";

// One shared socket for the whole chat page — reused across renders and by
// the notification system's personal room. Same instance the monolith used.
const socket = io(BASE_URL, {
  transports: ["websocket", "polling"],
});

function Chat() {
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [search, setSearch] = useState("");
  const [searchMsg, setSearchMsg] = useState("");
  const [showUsers, setShowUsers] = useState(true);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [onlineUsers, setOnlineUsers] = useState([]);
  const [isTyping, setIsTyping] = useState(false);
  const [showMsgSearch, setShowMsgSearch] = useState(false);
  const [contextMenu, setContextMenu] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState("");

  const [reactionPicker, setReactionPicker] = useState(null);

  // New-feature UI state.
  const [replyTarget, setReplyTarget] = useState(null);
  const [editTarget, setEditTarget] = useState(null);
  const [forwardState, setForwardState] = useState({ open: false, messages: [] });
  const [forwardSending, setForwardSending] = useState(false);
  const [disappearingSeconds, setDisappearingSeconds] = useState(0);
  const [highlightId, setHighlightId] = useState(null);
  const [showBackup, setShowBackup] = useState(false);

  // Chat permission (mutual-follow + not-blocked). When false the whole
  // composer is disabled and a notice explains why.
  const [canChat, setCanChat] = useState(true);
  const [chatNotice, setChatNotice] = useState("");

  // Block state for the currently open chat.
  const [blockInfo, setBlockInfo] = useState({ iBlocked: false, blocked: false });
  const [blockLoading, setBlockLoading] = useState(false);

  const userId = localStorage.getItem("userId");
  const token = localStorage.getItem("token");
  const username = localStorage.getItem("username");
  const bottomRef = useRef(null);
  const fileRef = useRef(null);
  const typingTimeout = useRef(null);
  const autoOpenedRef = useRef(false);
  const messageRefs = useRef({});

  // Voice / video calling (WebRTC) is owned app-wide by CallProvider so an
  // incoming call rings on any screen. Chat only STARTS outgoing calls and
  // reflects the active-call state to disable the call buttons.
  const { startCall, call: activeCall } = useCall();

  // Keep a live ref of the open chat so socket handlers (bound once) always
  // read the CURRENT partner without re-subscribing on every selection.
  const selectedRef = useRef(null);
  useEffect(() => {
    selectedRef.current = selectedUser;
  }, [selectedUser]);

  // ── PERMISSION / BLOCK ──────────────────────────────
  const verifyCanChat = useCallback(async (otherId) => {
    try {
      const res = await chatApi.checkCanChat(userId, otherId);
      const allowed = Boolean(res.data.canChat);
      setCanChat(allowed);
      setChatNotice(allowed ? "" : res.data.message || "");
    } catch {
      // Never lock a user out on a transient network error.
      setCanChat(true);
      setChatNotice("");
    }
  }, [userId]);

  const fetchBlockStatus = useCallback(async (otherId) => {
    if (!otherId || !token) return;
    try {
      const res = await chatApi.getBlockStatus(otherId, token);
      setBlockInfo({
        iBlocked: Boolean(res.data?.iBlocked),
        blocked: Boolean(res.data?.blocked),
      });
    } catch {
      setBlockInfo({ iBlocked: false, blocked: false });
    }
  }, [token]);

  const toggleBlock = async () => {
    if (!selectedUser || blockLoading || !token) return;
    setBlockLoading(true);
    try {
      if (blockInfo.iBlocked) {
        await chatApi.unblockUser(selectedUser._id, token);
      } else {
        await chatApi.blockUser(selectedUser._id, token);
      }
      await fetchBlockStatus(selectedUser._id);
      verifyCanChat(selectedUser._id);
    } catch (err) {
      console.error("Block toggle failed", err);
    } finally {
      setBlockLoading(false);
    }
  };

  // ── SOCKET SUBSCRIPTIONS ────────────────────────────
  useEffect(() => {
    if (!userId) return;

    socket.emit("join", userId);

    const onOnline = (list) => setOnlineUsers(list);

    const onReceive = (msg) => {
      const selected = selectedRef.current;
      const relevant =
        selected &&
        (String(msg.senderId) === String(selected._id) ||
          String(msg.receiverId) === String(selected._id));

      if (relevant) {
        setMessages((prev) => upsertMessage(prev, msg));
        if (String(msg.senderId) !== String(userId)) {
          socket.emit("messageRead", {
            msgId: msg._id,
            senderId: msg.senderId,
            receiverId: userId,
          });
        }
      }

      setUsers((prev) =>
        prev.map((u) => {
          if (u._id === msg.senderId || u._id === msg.receiverId) {
            const isCurrentChat = selected && u._id === selected._id;
            const isMine = String(msg.senderId) === String(userId);
            return {
              ...u,
              lastMessage: msg,
              unreadCount:
                isCurrentChat || isMine
                  ? u.unreadCount || 0
                  : (u.unreadCount || 0) + 1,
            };
          }
          return u;
        })
      );
    };

    const onTyping = ({ senderId }) => {
      if (String(senderId) === String(selectedRef.current?._id)) {
        setIsTyping(true);
        clearTimeout(typingTimeout.current);
        typingTimeout.current = setTimeout(() => setIsTyping(false), 2000);
      }
    };

    const onRead = ({ msgId }) => {
      setMessages((prev) =>
        prev.map((m) => (m._id === msgId ? { ...m, status: "read" } : m))
      );
    };

    const onDelivered = ({ msgId }) => {
      setMessages((prev) =>
        prev.map((m) =>
          m._id === msgId
            ? { ...m, status: m.status === "read" ? "read" : "delivered" }
            : m
        )
      );
    };

    const onDeleted = ({ msgId }) => {
      setMessages((prev) => prev.filter((m) => m._id !== msgId));
    };

    const onRelationship = ({ userId: otherId }) => {
      fetchUsers();
      const selected = selectedRef.current;
      if (selected && String(otherId) === String(selected._id)) {
        verifyCanChat(selected._id);
      }
    };

    // NEW: an edit was relayed — reconcile the bubble if it's in this thread.
    const onEdited = (msg) => {
      const selected = selectedRef.current;
      const relevant =
        selected &&
        (String(msg.senderId) === String(selected._id) ||
          String(msg.receiverId) === String(selected._id));
      if (relevant) setMessages((prev) => upsertMessage(prev, msg));
    };

    // NEW: reactions changed on a message — update just that bubble.
    const onReaction = ({ msgId, reactions }) => {
      setMessages((prev) =>
        prev.map((m) =>
          String(m._id) === String(msgId) ? { ...m, reactions } : m
        )
      );
    };

    // NEW: the disappearing timer changed for a conversation involving me.
    const onDisappearing = ({ userId: a, otherId: b, disappearingSeconds: secs }) => {
      const selected = selectedRef.current;
      if (
        selected &&
        (String(selected._id) === String(a) || String(selected._id) === String(b))
      ) {
        setDisappearingSeconds(secs);
      }
    };

    socket.on("onlineUsers", onOnline);
    socket.on("receiveMessage", onReceive);
    socket.on("userTyping", onTyping);
    socket.on("messageRead", onRead);
    socket.on("messageDelivered", onDelivered);
    socket.on("messageDeleted", onDeleted);
    socket.on("relationshipChanged", onRelationship);
    socket.on("messageEdited", onEdited);
    socket.on("messageReaction", onReaction);
    socket.on("disappearingChanged", onDisappearing);

    return () => {
      socket.off("onlineUsers", onOnline);
      socket.off("receiveMessage", onReceive);
      socket.off("userTyping", onTyping);
      socket.off("messageRead", onRead);
      socket.off("messageDelivered", onDelivered);
      socket.off("messageDeleted", onDeleted);
      socket.off("relationshipChanged", onRelationship);
      socket.off("messageEdited", onEdited);
      socket.off("messageReaction", onReaction);
      socket.off("disappearingChanged", onDisappearing);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  // Tell the server which conversation is on screen so it can suppress
  // notifications for messages we're already watching (WhatsApp behaviour the
  // backend was built for). Cleared automatically on switch / unmount.
  useEffect(() => {
    if (selectedUser) {
      socket.emit("openConversation", { withUserId: selectedUser._id });
      return () => socket.emit("closeConversation");
    }
  }, [selectedUser]);

  useEffect(() => {
    if (userId) fetchUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  useEffect(() => {
    const handleClick = () => setContextMenu(null);
    window.addEventListener("click", handleClick);
    return () => window.removeEventListener("click", handleClick);
  }, []);

  // ── DATA LOADING ────────────────────────────────────
  const fetchUsers = async () => {
    try {
      setLoadingUsers(true);
      const res = await chatApi.fetchUsers(userId);
      const list = res.data || [];
      setUsers(list);

      const targetId = new URLSearchParams(window.location.search).get("user");
      if (targetId && !autoOpenedRef.current) {
        const match = list.find((u) => String(u._id) === String(targetId));
        if (match) {
          autoOpenedRef.current = true;
          openChat(match);
        }
      }
    } catch (err) {
      console.log(err);
    } finally {
      setLoadingUsers(false);
    }
  };

  const openChat = async (user) => {
    setSelectedUser(user);
    setShowUsers(false);
    setShowMsgSearch(false);
    setReplyTarget(null);
    setEditTarget(null);
    setText("");
    setMessages([]);
    setCanChat(user.canChat !== false);
    setChatNotice("");
    setBlockInfo({
      iBlocked: user.iBlocked === true,
      blocked: user.canChat === false,
    });
    fetchBlockStatus(user._id);

    setUsers((prev) =>
      prev.map((u) => (u._id === user._id ? { ...u, unreadCount: 0 } : u))
    );

    try {
      const res = await chatApi.fetchMessages(userId, user._id);
      setMessages(res.data || []);
      socket.emit("markAllRead", { senderId: user._id, receiverId: userId });
    } catch (err) {
      console.log(err);
    }

    // Disappearing-timer state for the header indicator.
    chatApi
      .getConversationSettings(userId, user._id)
      .then((r) => setDisappearingSeconds(r.data?.disappearingSeconds || 0))
      .catch(() => setDisappearingSeconds(0));

    verifyCanChat(user._id);
  };

  const backToList = () => {
    setShowUsers(true);
    setSelectedUser(null);
  };

  // ── TYPING ──────────────────────────────────────────
  const handleTyping = (value) => {
    setText(value);
    if (selectedUser && canChat && !editTarget) {
      socket.emit("typing", { senderId: userId, receiverId: selectedUser._id });
    }
  };

  // ── SEND / DELIVER ──────────────────────────────────
  const buildReplyPayload = () => {
    if (!replyTarget) return undefined;
    return {
      messageId: replyTarget._id,
      senderName: replyTarget.__replyName || "",
      text: replyTarget.text || "",
      fileType: replyTarget.fileType || "",
    };
  };

  // Deliver an optimistic bubble (also used by retry). Preserves the original
  // precise error classification: 403 → real permission change (drop bubble +
  // banner); everything else → keep bubble as retryable, no blocking popup.
  const deliverMessage = async (optimistic) => {
    const payload = {
      clientId: optimistic.clientId,
      senderId: optimistic.senderId,
      receiverId: optimistic.receiverId,
      senderName: optimistic.senderName,
      text: optimistic.text,
      status: "sent",
    };
    if (optimistic.replyTo) payload.replyTo = optimistic.replyTo;

    try {
      const res = await chatApi.sendMessage(payload);
      const savedMsg = res.data;
      setMessages((prev) => upsertMessage(prev, savedMsg));
      socket.emit("sendMessage", savedMsg);
      return true;
    } catch (err) {
      const status = err.response?.status;
      const serverMsg = err.response?.data?.error;
      console.error("[chat] send ✗", {
        clientId: payload.clientId,
        httpStatus: status ?? "(no response — network/timeout)",
        serverError: serverMsg,
      });

      if (status === 403) {
        setCanChat(false);
        setChatNotice(serverMsg || "Chat is not available with this user.");
        setMessages((prev) =>
          prev.filter((m) => m.clientId !== optimistic.clientId)
        );
        return false;
      }

      setMessages((prev) =>
        prev.map((m) =>
          m.clientId === optimistic.clientId
            ? { ...m, status: "failed", failReason: serverMsg || null }
            : m
        )
      );
      return false;
    }
  };

  const sendMessage = async () => {
    if (editTarget) return submitEdit();
    if (!text.trim() || !selectedUser || !canChat) return;

    const replyTo = buildReplyPayload();
    const optimistic = {
      clientId: makeClientId(),
      senderId: userId,
      receiverId: selectedUser._id,
      senderName: username,
      text: text.trim(),
      createdAt: new Date().toISOString(),
      status: "sending",
      ...(replyTo ? { replyTo } : {}),
    };

    setMessages((prev) => [...prev, optimistic]);
    setText("");
    setReplyTarget(null);

    await deliverMessage(optimistic);
  };

  const retryMessage = async (failedMsg) => {
    setMessages((prev) =>
      prev.map((m) =>
        m.clientId === failedMsg.clientId ? { ...m, status: "sending" } : m
      )
    );
    await deliverMessage(failedMsg);
  };

  // ── FILE + VOICE UPLOAD ─────────────────────────────
  const uploadBlob = async (fileOrBlob, extra = {}) => {
    if (!selectedUser || !canChat) return;
    setUploading(true);
    setUploadError("");
    const clientId = makeClientId();
    const formData = new FormData();
    formData.append("file", fileOrBlob, extra.fileName || fileOrBlob.name);
    formData.append("senderId", userId);
    formData.append("receiverId", selectedUser._id);
    formData.append("clientId", clientId);
    if (extra.duration) formData.append("duration", String(extra.duration));
    const replyTo = buildReplyPayload();
    if (replyTo) formData.append("replyTo", JSON.stringify(replyTo));

    try {
      const res = await chatApi.uploadFile(formData);
      setMessages((prev) => upsertMessage(prev, res.data));
      socket.emit("sendMessage", res.data);
      setReplyTarget(null);
    } catch (err) {
      const status = err.response?.status;
      const serverMsg = err.response?.data?.error;
      console.error("[chat] upload ✗", {
        httpStatus: status ?? "(no response — network/timeout)",
        serverError: serverMsg,
      });
      if (status === 403) {
        setCanChat(false);
        setChatNotice(serverMsg || "Chat is not available with this user.");
      } else {
        setUploadError(
          serverMsg || "Upload failed — check the file size and your connection."
        );
      }
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    uploadBlob(file);
  };

  const sendVoiceMessage = (blob, duration) => {
    const ext = (blob.type.split("/")[1] || "webm").split(";")[0];
    uploadBlob(blob, { duration, fileName: `voice-${Date.now()}.${ext}` });
  };

  // ── REACTIONS (persisted) ───────────────────────────
  const addReaction = async (msgId, emoji) => {
    setReactionPicker(null);
    if (!msgId) return;

    const target = messages.find((m) => String(m._id) === String(msgId));
    const prevReactions = target?.reactions || [];
    const mineReaction = prevReactions.find(
      (r) => String(r.userId) === String(userId)
    );
    const toggledOff = mineReaction?.emoji === emoji;
    const nextEmoji = toggledOff ? "" : emoji;

    // Optimistic: replace my reaction locally (empty clears it — toggle off).
    setMessages((prev) =>
      prev.map((m) => {
        if (String(m._id) !== String(msgId)) return m;
        const others = (m.reactions || []).filter(
          (r) => String(r.userId) !== String(userId)
        );
        return {
          ...m,
          reactions: nextEmoji ? [...others, { userId, emoji: nextEmoji }] : others,
        };
      })
    );

    try {
      // Persist; the server broadcasts messageReaction to both sides, and the
      // response carries the authoritative reactions we reconcile onto.
      const res = await chatApi.reactToMessage(msgId, userId, nextEmoji);
      const reactions = res.data?.reactions || [];
      setMessages((prev) =>
        prev.map((m) =>
          String(m._id) === String(msgId) ? { ...m, reactions } : m
        )
      );
    } catch (err) {
      // Revert the optimistic change on failure.
      setMessages((prev) =>
        prev.map((m) =>
          String(m._id) === String(msgId) ? { ...m, reactions: prevReactions } : m
        )
      );
      const serverMsg = err.response?.data?.error;
      console.error("[chat] reaction ✗", serverMsg || err.message);
      // Reverting silently is what made a failed reaction look like a bug: the
      // emoji appeared and vanished with nothing to explain it. Surface it in
      // the same dismissible banner the composer already uses for send/edit
      // errors so the user knows it did not save.
      setUploadError(
        serverMsg || "Reaction not saved — check your connection and try again."
      );
    }
  };

  // ── EDIT ────────────────────────────────────────────
  const startEdit = (msg) => {
    setEditTarget(msg);
    setReplyTarget(null);
    setText(msg.text || "");
    setContextMenu(null);
  };

  const cancelEdit = () => {
    setEditTarget(null);
    setText("");
  };

  const submitEdit = async () => {
    if (!editTarget || !text.trim()) return;
    const newText = text.trim();
    const msgId = editTarget._id;
    setEditTarget(null);
    setText("");
    try {
      const res = await chatApi.editMessage(msgId, userId, newText);
      setMessages((prev) => upsertMessage(prev, res.data));
      socket.emit("editMessage", res.data);
    } catch (err) {
      console.error("[chat] edit ✗", err.response?.data?.error || err.message);
      setUploadError(err.response?.data?.error || "Could not edit the message.");
    }
  };

  // ── DELETE ──────────────────────────────────────────
  const deleteMessage = async (msgId) => {
    try {
      await chatApi.deleteMessage(msgId);
      socket.emit("deleteMessage", { msgId, receiverId: selectedUser._id });
      setMessages((prev) => prev.filter((m) => m._id !== msgId));
    } catch (err) {
      console.log(err);
    }
    setContextMenu(null);
  };

  // ── REPLY / FORWARD ─────────────────────────────────
  const displayNameFor = (msg) =>
    String(msg.senderId) === String(userId)
      ? "You"
      : selectedUser?.name || "Message";

  const startReply = (msg) => {
    setReplyTarget({ ...msg, __replyName: displayNameFor(msg) });
    setEditTarget(null);
    setContextMenu(null);
  };

  const openForward = (msg) => {
    setForwardState({ open: true, messages: [msg] });
    setContextMenu(null);
  };

  const forwardMessages = async (targetIds) => {
    const toSend = forwardState.messages;
    if (!targetIds.length || !toSend.length) return;
    setForwardSending(true);
    try {
      for (const targetId of targetIds) {
        for (const src of toSend) {
          const payload = {
            clientId: makeClientId(),
            senderId: userId,
            receiverId: targetId,
            senderName: username,
            forwarded: true,
            text: src.fileUrl ? "" : src.text || "",
            fileUrl: src.fileUrl || "",
            fileType: src.fileType || "",
            fileName: src.fileName || "",
            duration: src.duration || 0,
            status: "sent",
          };
          try {
            const res = await chatApi.sendMessage(payload);
            socket.emit("sendMessage", res.data);
            // If forwarding into the open chat, show it immediately.
            if (selectedUser && String(targetId) === String(selectedUser._id)) {
              setMessages((prev) => upsertMessage(prev, res.data));
            }
          } catch (err) {
            console.error("[chat] forward ✗", err.response?.data?.error || err.message);
          }
        }
      }
    } finally {
      setForwardSending(false);
      setForwardState({ open: false, messages: [] });
    }
  };

  // ── DISAPPEARING TIMER ──────────────────────────────
  const setDisappearingTimer = async (seconds) => {
    if (!selectedUser) return;
    const prev = disappearingSeconds;
    setDisappearingSeconds(seconds);
    try {
      await chatApi.setDisappearing(userId, selectedUser._id, seconds);
    } catch (err) {
      setDisappearingSeconds(prev);
      console.error("[chat] disappearing ✗", err.response?.data?.error || err.message);
    }
  };

  // ── NAVIGATION HELPERS ──────────────────────────────
  const scrollToMessage = (messageId) => {
    const el = messageRefs.current[messageId];
    if (!el) return;
    el.scrollIntoView({ behavior: "smooth", block: "center" });
    setHighlightId(messageId);
    setTimeout(() => setHighlightId(null), 1500);
  };

  const handleContextMenu = (e, msg) => {
    e.preventDefault();
    setContextMenu({ x: e.pageX, y: e.pageY, msg });
  };

  const filteredMsgs = searchMsg
    ? messages.filter((m) =>
        m.text?.toLowerCase().includes(searchMsg.toLowerCase())
      )
    : messages;

  return (
    <>
      <Navbar />

      <div className="chat-page">
        <div className={`chat-users ${showUsers ? "show-users" : "hide-users"}`}>
          <UserList
            users={users}
            loading={loadingUsers}
            search={search}
            onSearch={setSearch}
            selectedUserId={selectedUser?._id}
            onlineUsers={onlineUsers}
            onOpen={openChat}
          />
        </div>

        <div className={`chat-box ${!showUsers ? "show-chat" : "hide-chat"}`}>
          {!selectedUser ? (
            <div className="chat-empty">
              <div className="empty-icon">💬</div>
              <p>Select a user to start chatting</p>
            </div>
          ) : (
            <>
              <ChatHeader
                user={selectedUser}
                online={onlineUsers.includes(selectedUser._id)}
                blockInfo={blockInfo}
                blockLoading={blockLoading}
                onToggleBlock={toggleBlock}
                onToggleSearch={() => setShowMsgSearch((v) => !v)}
                onBack={backToList}
                disappearingSeconds={disappearingSeconds}
                onSetDisappearing={setDisappearingTimer}
                onVoiceCall={() => startCall(selectedUser, "voice")}
                onVideoCall={() => startCall(selectedUser, "video")}
                onOpenBackup={() => setShowBackup(true)}
                callDisabled={!canChat || activeCall.state !== "idle"}
              />

              {showMsgSearch && (
                <div className="msg-search-bar">
                  <input
                    placeholder="Search in chat..."
                    value={searchMsg}
                    onChange={(e) => setSearchMsg(e.target.value)}
                    autoFocus
                  />
                  <button
                    onClick={() => {
                      setShowMsgSearch(false);
                      setSearchMsg("");
                    }}
                  >
                    ✕
                  </button>
                </div>
              )}

              <div className="chat-messages">
                {isTyping && (
                  <div className="typing-indicator">
                    <span></span>
                    <span></span>
                    <span></span>
                  </div>
                )}

                {uploading && (
                  <div className="upload-indicator">
                    <span>Uploading file... ⏳</span>
                  </div>
                )}

                {uploadError && (
                  <div className="upload-error" onClick={() => setUploadError("")}>
                    ⚠ {uploadError} <small>(tap to dismiss)</small>
                  </div>
                )}

                {filteredMsgs.map((m, i) => (
                  <MessageBubble
                    key={m.clientId || m._id || i}
                    msg={m}
                    mine={String(m.senderId) === String(userId)}
                    highlighted={highlightId && String(highlightId) === String(m._id)}
                    registerRef={(el) => {
                      if (el && m._id) messageRefs.current[m._id] = el;
                    }}
                    reactionPickerOpen={reactionPicker === m._id}
                    onContextMenu={handleContextMenu}
                    onToggleReactionPicker={(id) =>
                      setReactionPicker((cur) => (cur === id ? null : id))
                    }
                    onReact={addReaction}
                    onReplyJump={scrollToMessage}
                    onRetry={retryMessage}
                  />
                ))}

                <div ref={bottomRef} />
              </div>

              {contextMenu && (
                <div
                  className="context-menu"
                  style={{ top: contextMenu.y, left: contextMenu.x }}
                >
                  <button onClick={() => startReply(contextMenu.msg)}>
                    ↩ Reply
                  </button>
                  <button onClick={() => openForward(contextMenu.msg)}>
                    ↪ Forward
                  </button>
                  {String(contextMenu.msg.senderId) === String(userId) &&
                    !contextMenu.msg.fileUrl && (
                      <button onClick={() => startEdit(contextMenu.msg)}>
                        ✏️ Edit
                      </button>
                    )}
                  {String(contextMenu.msg.senderId) === String(userId) && (
                    <button
                      className="ctx-delete"
                      onClick={() => deleteMessage(contextMenu.msg._id)}
                    >
                      🗑 Delete
                    </button>
                  )}
                </div>
              )}

              {!canChat && (
                <div className="chat-disabled-banner">
                  🚫 {chatNotice || "Chat is not available with this user."}
                </div>
              )}

              <MessageInput
                text={text}
                onTextChange={handleTyping}
                onSend={sendMessage}
                canChat={canChat}
                uploading={uploading}
                fileRef={fileRef}
                onFileChange={handleFileUpload}
                replyTarget={replyTarget}
                onCancelReply={() => setReplyTarget(null)}
                editTarget={editTarget}
                onCancelEdit={cancelEdit}
                onSendVoice={sendVoiceMessage}
              />
            </>
          )}
        </div>
      </div>

      {forwardState.open && (
        <ForwardModal
          users={users}
          messages={forwardState.messages}
          sending={forwardSending}
          onClose={() => setForwardState({ open: false, messages: [] })}
          onForward={forwardMessages}
        />
      )}

      {showBackup && (
        <BackupDialog userId={userId} onClose={() => setShowBackup(false)} />
      )}
    </>
  );
}

export default Chat;
